// HTML for the admin Dashboard. Dependency-free: inline SVG and CSS bars, no chart library.
// One accent for "this period", zinc for context. Everything user-supplied goes through esc().
import { change } from './adminInsights.js';

const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const TZ = 'Asia/Kolkata';
const dayFmt = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: TZ });
const hourFmt = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', timeZone: TZ });
const compactFmt = new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 });

export const money = n => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const num = n => (Number(n) || 0).toLocaleString('en-IN');
const compactMoney = n => `₹${compactFmt.format(n)}`;
const plural = (n, one, many = one + 's') => `${num(n)} ${n === 1 ? one : many}`;
const STATUS = { pending: 'Pending', confirmed: 'Confirmed', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled' };
const ago = iso => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? 'just now' : m < 60 ? `${m}m ago` : m < 48 * 60 ? `${Math.round(m / 60)}h ago` : dayFmt.format(new Date(iso));
};
const hourLabel = h => `${h % 12 || 12}${h < 12 ? ' AM' : ' PM'}`;

const ICON = {
  up: '<path d="M7 17 17 7M8 7h9v9"/>', down: '<path d="m7 7 10 10M17 8v9H8"/>', flat: '<path d="M5 12h14"/>',
  refresh: '<path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7"/>',
  chart: '<path d="M3 3v18h18M7 15l4-4 3 3 5-6"/>', box: '<path d="M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8"/>',
  pin: '<path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/>',
  ok: '<circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/>', ship: '<rect x="1" y="6" width="13" height="10" rx="1"/><path d="M14 9h4l3 3v4h-7z"/><circle cx="5.5" cy="18" r="1.8"/><circle cx="17.5" cy="18" r="1.8"/>',
  msg: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>', star: '<path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.3-6.2 3.3 1.2-6.8-5-4.9 6.9-1z"/>',
  caret: '<path d="m9 6 6 6-6 6"/>',
};
const svg = (name, size = 16) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[name]}</svg>`;

/** Top of a four-step axis whose every step is a tidy number (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 x 10^n). */
function niceMax(max) {
  if (max <= 0) return 4;
  const raw = max / 4, pow = 10 ** Math.floor(Math.log10(raw));
  return [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(v => v * pow >= raw - 1e-9) * pow * 4;
}

/* ── primitives ─────────────────────────────────────────────────────────── */

function delta(now, prev) {
  const c = change(now, prev);
  if (!c) return '';
  const pct = Math.abs(c.pct);
  return `<span class="an-delta an-delta--${c.dir}">${svg(c.dir, 12)}${pct >= 1000 ? '999+' : pct.toFixed(1)}%<span class="an-sr"> ${c.dir === 'flat' ? 'unchanged' : c.dir} on the previous period</span></span>`;
}

function sparkline(values, tone = 'accent') {
  if (values.length < 2) return '<div class="an-spark"></div>';
  const max = Math.max(...values), min = Math.min(...values), span = max - min || 1;
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${((i / (values.length - 1)) * 100).toFixed(2)} ${(94 - ((v - min) / span) * 84).toFixed(2)}`).join('');
  return `<svg class="an-spark an-spark--${tone}" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d="${line}L100 100L0 100Z" fill="currentColor" opacity=".09"/><path d="${line}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/></svg>`;
}

function kpi({ label, value, deltaHtml = '', hint = '', spark = '', compact = false }) {
  return `<div class="an-kpi${compact ? ' an-kpi--compact' : ''}">
    <div class="an-kpi-top"><span class="an-kpi-label">${esc(label)}</span>${deltaHtml}</div>
    <span class="an-kpi-value">${value}</span>${spark}${hint ? `<span class="an-kpi-hint">${hint}</span>` : ''}
  </div>`;
}

const cardHead = (title, description = '', actions = '') => `<div class="an-head"><div><h3 class="an-title">${esc(title)}</h3>${description ? `<p class="an-desc">${description}</p>` : ''}</div>${actions}</div>`;
const empty = (icon, title, hint = '') => `<div class="an-empty"><span class="an-empty-ico">${svg(icon, 18)}</span><p class="an-empty-title">${esc(title)}</p>${hint ? `<p class="an-empty-hint">${esc(hint)}</p>` : ''}</div>`;

/** Ranked list: label and figure on one line, a thin proportional track beneath. Leader gets the accent. */
function rankBars(rows, { ranked = false } = {}) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return `<ol class="an-rank">${rows.map((r, i) => `<li class="an-rank-row">
    ${ranked ? `<span class="an-rank-n">${i + 1}</span>` : ''}
    <div class="an-rank-main">
      <div class="an-rank-line">
        <span class="an-rank-label"><span class="an-rank-name">${r.label}</span>${r.sub ? `<span class="an-rank-sub">${r.sub}</span>` : ''}</span>
        <span class="an-rank-val">${r.display}</span>
      </div>
      <span class="an-track" aria-hidden="true"><span class="an-fill ${r.bar || (i === 0 ? 'an-fill--lead' : '')}" style="width:${Math.max(1.5, (r.value / max) * 100).toFixed(1)}%;animation-delay:${i * 60}ms"></span></span>
    </div>
  </li>`).join('')}</ol>`;
}

function hourStrip(values) {
  const max = Math.max(1, ...values), top = Math.max(...values), peak = values.indexOf(top), has = top > 0;
  return `<div class="an-hours">
    <p class="an-desc">${has ? `Busiest hour <strong>${hourLabel(peak)}</strong> with <strong>${num(top)}</strong> ${top === 1 ? 'order' : 'orders'}` : 'No orders in this period'}</p>
    <div class="an-hour-bars" role="img" aria-label="Orders by hour of day, India time">${values.map((v, h) =>
      `<span class="an-hour-col" title="${hourLabel(h)}: ${v}"><span class="an-hour-bar${has && h === peak ? ' is-peak' : ''}" style="height:${Math.max(4, (v / max) * 100).toFixed(1)}%;animation-delay:${h * 18}ms"></span></span>`).join('')}</div>
    <div class="an-hour-axis">${[0, 6, 12, 18].map(h => `<span>${hourLabel(h)}</span>`).join('')}<span>11 PM</span></div>
  </div>`;
}

/** Area chart with a dashed previous-period line. Hover is wired by wireTrendCharts(). */
function trendChart({ points, prev, hourly, format, axis, label, prevLabel }) {
  const count = points.length;
  const top = niceMax(Math.max(0, ...points.map(p => p.value), ...(prev || [])));
  const x = i => (count > 1 ? (i / (count - 1)) * 100 : 50);
  const y = v => 100 - (v / top) * 100;
  const path = vals => vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join('');
  const cur = points.map(p => p.value);
  const when = t => (hourly ? hourFmt.format(t).toUpperCase() : dayFmt.format(t));
  const hot = points.map((p, i) => ({
    x: +x(i).toFixed(2), y: +(100 - y(p.value)).toFixed(2),
    when: hourly ? `Today, ${when(p.t)}` : when(p.t), v: format(p.value), p: prev ? format(prev[i] || 0) : null,
  }));
  const at = [0, Math.round((count - 1) / 3), Math.round(((count - 1) * 2) / 3), count - 1].filter((v, i, a) => a.indexOf(v) === i);
  return `<figure class="an-trend" data-an-trend="${esc(JSON.stringify({ hot, prevLabel }))}">
    <div class="an-plot-wrap"><div class="an-plot">
      ${[0, 0.25, 0.5, 0.75, 1].map(t => `<div class="an-grid${t === 0 ? ' an-grid--base' : ''}" style="bottom:${t * 100}%"><span class="an-tick">${axis(top * t)}</span></div>`).join('')}
      <svg class="an-plot-svg" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="${esc(label)}">
        <defs><linearGradient id="an-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="currentColor" stop-opacity=".2"/><stop offset="100%" stop-color="currentColor" stop-opacity="0"/></linearGradient></defs>
        ${count > 1 ? `<path d="${path(cur)}L100 100L0 100Z" fill="url(#an-area)"/>` : ''}
        ${prev && count > 1 ? `<path d="${path(prev.slice(0, count))}" fill="none" stroke="#a1a1aa" stroke-width="1.5" stroke-dasharray="4 4" vector-effect="non-scaling-stroke" stroke-linejoin="round"/>` : ''}
        ${count > 1 ? `<path d="${path(cur)}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>` : ''}
      </svg>
      <div class="an-cross" hidden></div><div class="an-dot" hidden></div>
      <div class="an-tip" role="status" hidden></div>
    </div></div>
    <div class="an-xaxis">${at.map(i => `<span>${when(points[i].t)}</span>`).join('')}</div>
    <table class="an-sr"><caption>${esc(label)}</caption><tbody>${points.map((p, i) => `<tr><th scope="row">${hot[i].when}</th><td>${hot[i].v}</td></tr>`).join('')}</tbody></table>
  </figure>`;
}

/** Hover crosshair + tooltip for every trend chart under `root`. Call after each render. */
export function wireTrendCharts(root) {
  root.querySelectorAll('[data-an-trend]').forEach(fig => {
    const { hot, prevLabel } = JSON.parse(fig.dataset.anTrend);
    const plot = fig.querySelector('.an-plot'), cross = fig.querySelector('.an-cross'), dot = fig.querySelector('.an-dot'), tip = fig.querySelector('.an-tip');
    const show = on => { cross.hidden = dot.hidden = tip.hidden = !on; };
    plot.addEventListener('pointermove', e => {
      const r = plot.getBoundingClientRect();
      const i = Math.min(hot.length - 1, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * (hot.length - 1))));
      const p = hot[i];
      cross.style.left = dot.style.left = tip.style.left = `${p.x}%`;
      dot.style.bottom = `${p.y}%`;
      tip.style.transform = `translateX(${p.x < 18 ? '0%' : p.x > 82 ? '-100%' : '-50%'})`;
      tip.innerHTML = `<span class="an-tip-when">${esc(p.when)}</span><span class="an-tip-val">${esc(p.v)}</span>${p.p != null ? `<span class="an-tip-prev">${esc(prevLabel)}: ${esc(p.p)}</span>` : ''}`;
      show(true);
    });
    plot.addEventListener('pointerleave', () => show(false));
  });
}

/* ── the page ───────────────────────────────────────────────────────────── */

export const RANGES = [[0, 'Today'], [7, '7d'], [30, '30d'], [90, '90d']];
const PIPELINE = [['pending', 'Pending'], ['confirmed', 'Confirmed'], ['shipped', 'Shipped'], ['delivered', 'Delivered'], ['cancelled', 'Cancelled']];
const PAYMENTS = [['paid', 'Paid'], ['failed', 'Failed'], ['pending', 'Not completed']];

/** @param {ReturnType<import('./adminInsights.js').buildInsights>} d */
export function dashboardPage(d, { metric = 'revenue', stamp = '' } = {}) {
  const { cur, prev, today } = d;
  const first = d.cur.series[0]?.t, last = d.cur.series.at(-1)?.t;
  const range = today ? `Today · ${dayFmt.format(d.until)}` : first ? `${dayFmt.format(first)} – ${dayFmt.format(last)}` : '';
  const isRev = metric === 'revenue';
  const fmt = isRev ? money : num;
  const points = cur.series.map(s => ({ t: s.t, value: isRev ? s.revenue : s.orders }));
  const prevVals = prev.series.map(s => (isRev ? s.revenue : s.orders));
  const total = points.reduce((s, p) => s + p.value, 0);
  const prevTotal = isRev ? prev.revenue : prev.orders;
  const a = d.attention;
  const open = a.toShip + a.messages + a.reviews + a.stock;
  const payN = s => d.payments.find(p => p.status === s)?.orders || 0;

  const attention = [
    ['Orders to ship', a.toShip, 'orders', 'ship'], ['New messages', a.messages, 'messages', 'msg'],
    ['Reviews to moderate', a.reviews, 'reviews', 'star'], ['Products low or out of stock', a.stock, 'products', 'box'],
  ].sort((x, y) => y[1] - x[1]);

  const pipeline = PIPELINE.filter(([k]) => d.pipeline.some(p => p.status === k)).map(([k, label]) => {
    const n = d.pipeline.find(p => p.status === k).orders;
    return { label, value: n, display: num(n), bar: `an-fill--${k}` };
  });

  return `
  <div class="an-page">
    <div class="adm-overview-hd an-hd">
      <div>
        <p class="an-eyebrow"><span class="an-live"></span>${esc(range)}${stamp ? `<span class="an-stamp"> · updated ${esc(stamp)}</span>` : ''}</p>
        <h1 class="adm-overview-title">Overview</h1>
        <p class="adm-overview-sub">Paid orders only. Refunds and cancellations are excluded from revenue.</p>
      </div>
      <div class="adm-title-actions">
        <button class="an-icon-btn" data-an-refresh type="button" aria-label="Refresh">${svg('refresh', 16)}</button>
        <div class="an-seg" role="group" aria-label="Date range">${RANGES.map(([v, l]) => `<button type="button" data-an-range="${v}" aria-pressed="${v === d.days}">${l}</button>`).join('')}</div>
      </div>
    </div>

    <div class="an-kpis an-kpis--lead">
      ${kpi({ label: 'Revenue', value: money(cur.revenue), deltaHtml: delta(cur.revenue, prev.revenue), spark: sparkline(cur.series.map(s => s.revenue)) })}
      ${kpi({ label: 'Orders', value: num(cur.orders), deltaHtml: delta(cur.orders, prev.orders), spark: sparkline(cur.series.map(s => s.orders), 'muted') })}
      ${kpi({ label: 'Avg. order value', value: money(cur.aov), deltaHtml: delta(cur.aov, prev.aov), spark: sparkline(cur.series.map(s => (s.orders ? s.revenue / s.orders : 0)), 'muted') })}
      ${kpi({ label: 'New customers', value: num(cur.newCustomers), deltaHtml: delta(cur.newCustomers, prev.newCustomers), hint: `${num(d.customers)} in total` })}
    </div>
    <div class="an-kpis">
      ${kpi({ compact: true, label: 'Items sold', value: num(d.itemsSold), hint: `across ${plural(cur.orders, 'order')}` })}
      ${kpi({ compact: true, label: 'Repeat buyers', value: d.buyers ? `${((d.repeatBuyers / d.buyers) * 100).toFixed(1)}%` : '—', hint: `${num(d.repeatBuyers)} of ${num(d.buyers)} ordered again` })}
      ${kpi({ compact: true, label: 'Refunds', value: money(d.refunds.amount), hint: `${plural(d.refunds.orders, 'order')} refunded` })}
      ${kpi({ compact: true, label: 'Customers', value: num(d.customers), hint: 'registered accounts' })}
    </div>

    <div class="an-row an-row--trend">
      <section class="an-panel">
        ${cardHead(isRev ? 'Revenue' : 'Orders', today ? 'Against yesterday, up to the same time of day' : 'Against the previous period of the same length',
          `<div class="an-seg an-seg--sm" role="group" aria-label="Chart metric">${[['revenue', 'Revenue'], ['orders', 'Orders']].map(([v, l]) => `<button type="button" data-an-metric="${v}" aria-pressed="${v === metric}">${l}</button>`).join('')}</div>`)}
        <div class="an-total"><span class="an-total-val">${fmt(total)}</span>${delta(total, prevTotal)}
          <span class="an-legend"><span><i class="an-key an-key--cur"></i>${today ? 'Today' : 'This period'}</span><span><i class="an-key an-key--prev"></i>${today ? 'Yesterday' : 'Previous'}</span></span></div>
        ${total > 0
          ? trendChart({ points, prev: prevVals, hourly: today, format: fmt, axis: isRev ? compactMoney : n => compactFmt.format(n), label: `${today ? 'Hourly' : 'Daily'} ${metric}`, prevLabel: today ? 'Yesterday' : 'Before' })
          : empty('chart', today ? 'No paid orders yet today' : 'No paid orders in this period', `Once orders are paid they show up here, ${today ? 'hour by hour' : 'day by day'}.`)}
      </section>

      <section class="an-panel">
        ${cardHead('Needs attention', open ? `${plural(open, 'item')} waiting` : 'Nothing is waiting on you')}
        ${open === 0
          ? empty('ok', 'All caught up', 'No orders to ship, messages, reviews or stock alerts are waiting.')
          : `<ul class="an-attn">${attention.map(([label, n, tab, ico]) => `<li><button type="button" class="an-attn-row" data-adm-tab="${tab}">
              <span class="an-attn-ico${n ? ' is-on' : ''}">${svg(ico, 16)}</span><span class="an-attn-label${n ? '' : ' is-off'}">${label}</span>
              <span class="an-attn-n${n ? ' is-on' : ''}">${num(n)}</span><span class="an-attn-caret">${svg('caret', 14)}</span></button></li>`).join('')}</ul>`}
      </section>
    </div>

    <section class="an-panel">
      ${cardHead('Recent orders', 'Latest paid orders', '<button type="button" class="adm-card-link" data-adm-tab="orders">View all</button>')}
      ${d.recentOrders.length
        ? `<ul class="an-orders">${d.recentOrders.map(o => `<li><button type="button" class="an-order" data-adm-open-order="${esc(o.id)}">
            <span class="an-order-id">#${esc(String(o.id).slice(0, 8).toUpperCase())}</span>
            <span class="an-order-who"><span class="an-rank-name">${esc(o.full_name)}</span><span class="an-rank-sub">${esc(o.city)}</span></span>
            <span class="an-order-total">${money(o.total)}</span>
            <span class="an-order-status"><span class="adm-badge adm-badge--${esc(o.status)}">${esc(STATUS[o.status] || o.status)}</span></span>
            <span class="an-order-ago">${ago(o.created_at)}</span></button></li>`).join('')}</ul>`
        : empty('ship', 'No orders yet', 'New paid orders appear here as they come in.')}
    </section>

    <div class="an-row an-row--products">
      <section class="an-panel">
        ${cardHead('Top products', 'By revenue, paid orders', '<button type="button" class="adm-card-link" data-adm-tab="products">All products</button>')}
        ${d.topProducts.length
          ? rankBars(d.topProducts.map(p => ({ label: esc(p.name), sub: plural(p.units, 'unit'), value: p.revenue, display: money(p.revenue) })), { ranked: true })
          : empty('box', 'No product sales yet', 'Best sellers rank here once orders are paid.')}
      </section>

      <section class="an-panel">
        ${cardHead('Order pipeline', 'Paid orders by fulfilment status')}
        ${pipeline.length ? rankBars(pipeline) : empty('ship', 'No paid orders in this period')}
        <dl class="an-trio">${PAYMENTS.map(([k, l]) => `<div><dt>${l}</dt><dd>${num(payN(k))}</dd></div>`).join('')}</dl>
      </section>
    </div>

    <div class="an-row an-row--geo">
      <section class="an-panel an-panel--stack">
        <div>
          ${cardHead('Top states', 'Where paid orders ship to')}
          ${d.topStates.length
            ? rankBars(d.topStates.slice(0, 5).map(s => ({ label: `<span class="an-state">${svg('pin', 13)}${esc(s.name)}</span>`, sub: plural(s.orders, 'order'), value: s.revenue, display: money(s.revenue) })))
            : empty('pin', 'No orders yet')}
        </div>
        ${d.topCategories.length ? `<div class="an-sep">${cardHead('Top categories')}${rankBars(d.topCategories.slice(0, 4).map(c => ({ label: esc(c.name), value: c.revenue, display: money(c.revenue) })))}</div>` : ''}
      </section>

      <section class="an-panel">
        ${cardHead('Low stock', 'Set the new quantity to restock', '<button type="button" class="adm-card-link" data-adm-tab="products">Manage</button>')}
        ${d.lowStock.length
          ? `<ul class="an-stock">${d.lowStock.map(p => `<li><span class="an-stock-name">${esc(p.name)}</span><span class="an-stock-n${p.stock_qty <= 0 ? ' is-out' : ''}">${p.stock_qty <= 0 ? 'Out' : `${num(p.stock_qty)} left`}</span>
            <span class="an-restock"><input type="number" min="1" value="${p.stock_qty <= 0 ? 50 : 100}" data-an-restock-id="${p.id}" aria-label="New stock for ${esc(p.name)}"><button type="button" data-an-restock-save="${p.id}">Set</button></span></li>`).join('')}</ul>${d.stockCount > d.lowStock.length ? `<p class="an-more">and ${num(d.stockCount - d.lowStock.length)} more</p>` : ''}`
          : empty('ok', 'Stock looks healthy', 'Nothing is at or below its alert level.')}
      </section>
    </div>

    <section class="an-panel">
      ${cardHead('Orders by hour', 'Time of day, India time')}
      ${hourStrip(d.byHour)}
    </section>
  </div>`;
}
